import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type { ComboboxItem } from '../Combobox/Combobox'
import { SearchSelect } from '../SearchSelect/SearchSelect'
import type { PickerValue } from '../ProductPicker/ProductPicker'

interface CountryRow {
  id: number
  name: string
  code: string
}

interface CountriesResponse {
  countries: CountryRow[]
}

export interface CountryPickerProps {
  value: PickerValue
  onChange: (next: PickerValue) => void
  placeholder?: string
  editorMode?: boolean
  onCancel?: () => void
  width?: number
  className?: string
  /** 'field' = bordered form input; 'cell' (default) = borderless, for grids. */
  variant?: 'cell' | 'field'
  disabled?: boolean
}

/**
 * Country picker backed by `/api/pricelists/search/countries` with an
 * empty query — Odoo's res.country is ~250 rows, small enough to load
 * once and filter client-side. Per Sprint 01 D-7: global TanStack Query
 * cache (`staleTime: 24h`, `gcTime: Infinity`) so the list survives
 * page navigation within the session.
 */
export function CountryPicker({
  value,
  onChange,
  placeholder = 'Select country…',
  editorMode = false,
  onCancel,
  width = 320,
  className,
  variant = 'cell',
  disabled,
}: CountryPickerProps) {
  // The full ~250-row list is loaded once; SearchSelect/cmdk filters it
  // client-side by the typed text, so this picker never re-fetches on input
  // and doesn't need to track the query string itself.
  const { data } = useQuery<CountriesResponse>({
    queryKey: ['countries', 'all'],
    queryFn: () => jsonFetch<CountriesResponse>('/api/pricelists/search/countries'),
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: Infinity,
  })

  const items: ComboboxItem[] = useMemo(
    () =>
      (data?.countries ?? []).map((c) => ({
        value: String(c.id),
        label: c.name,
        meta: c.code || undefined,
        // cmdk's built-in filter matches against `value` + `keywords` only
        // (not the rendered label). `value` here is the id, so the name +
        // code must be in `keywords` for type-ahead to work.
        keywords: [c.name, c.code].filter(Boolean),
      })),
    [data],
  )

  const handlePicked = (idStr: string) => {
    const id = Number(idStr)
    const row = (data?.countries ?? []).find((c) => c.id === id)
    if (!row) return
    onChange([id, row.name])
  }

  return (
    <SearchSelect
      variant={variant}
      items={items}
      value={value ? String(value[0]) : undefined}
      selectedLabel={value ? value[1] : null}
      onQueryChange={() => {}}
      onSelect={handlePicked}
      onClear={() => onChange(null)}
      editorMode={editorMode}
      onCancel={onCancel}
      width={width}
      placeholder={placeholder}
      emptyMessage={data ? 'No matches' : 'Loading…'}
      shouldFilter
      disabled={disabled}
      className={className}
    />
  )
}
